import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import type {
  PlayEvent,
  PlayJoinPolicy,
  PublicPlayEventParticipant,
  Region,
  User,
} from '@court-sync/shared';
import { isValidPhone, normalizePhoneToE164, resolvePlayEventPrice } from '@court-sync/shared';
import {
  Alert,
  AlertDescription,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  PhoneInput,
  PhoneNumberLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { PublicStripePaymentConfirm } from '@/components/public/PublicStripePaymentConfirm';
import { useAuthStore } from '@/store/auth';
import {
  playEventService,
  type MyPlayEventRegistration,
  type PlayEventJoinPayload,
  type PlayEventJoinResponse,
} from '@/services/playEvent';
import { publicClubMembershipService } from '@/services/publicClubMembership';
import { studentService } from '@/services/student';
import { academyProfileService } from '@/services/academyProfile';
import { getCurrencyFromRegion } from '@/utils/currency';
import { OrgSignInLink } from '@/components/public/org/OrgSignInLink';
import { useOrgRegisterDialogOptional } from '@/contexts/OrgRegisterDialogContext';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import { cn } from '@/lib/utils';
import type { OrgRegisterMode } from '@/contexts/OrgRegisterDialogContext';
import {
  playEventNeedsAcademyStudent,
  resolvePlayEventRegisterMode,
} from '@/utils/playEventRegistration';

type JoinMode = 'member' | 'student' | 'guest';
type ParticipantGender = 'male' | 'female' | 'other';

interface JoinModeOption {
  mode: JoinMode;
  label: string;
  memberId?: string;
  studentId?: string;
}

interface PublicPlayEventJoinSectionProps {
  orgSlug: string;
  eventSlug: string;
  event: PlayEvent;
  participants: PublicPlayEventParticipant[];
  clubSlug?: string | null;
  academyId?: string | null;
  orgRegion?: Region | string | null;
  pathFor: (subpath: string) => string;
  onJoined: () => void;
}

function unpaidRegistrationMessage(
  event: PlayEvent,
  registration: Pick<MyPlayEventRegistration, 'participantType' | 'paymentStatus'>
): string | null {
  const price = resolvePlayEventPrice({
    event,
    participantType: registration.participantType,
  });
  if (price.finalPriceCents <= 0 || registration.paymentStatus !== 'unpaid') {
    return null;
  }
  if (event.paymentMode === 'at_door') {
    return ' — payment is due at the door.';
  }
  return ' — you are registered, but payment is still due. Follow the payment instructions from the club or academy.';
}

function allowsGuest(joinPolicy: PlayJoinPolicy): boolean {
  return joinPolicy === 'open';
}

function allowsMember(joinPolicy: PlayJoinPolicy): boolean {
  return joinPolicy !== 'students_only';
}

function allowsStudent(joinPolicy: PlayJoinPolicy): boolean {
  return joinPolicy !== 'members_only';
}

type GuestContactDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

function resolveGuestContactFromUser(user: User): GuestContactDetails {
  return {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    phone: user.phone ?? '',
  };
}

function applyGuestContactDetails(
  contact: GuestContactDetails,
  setters: {
    setGuestFirstName: (value: string) => void;
    setGuestLastName: (value: string) => void;
    setGuestEmail: (value: string) => void;
    setGuestPhone: (value: string) => void;
  }
) {
  setters.setGuestFirstName(contact.firstName);
  setters.setGuestLastName(contact.lastName);
  setters.setGuestEmail(contact.email);
  setters.setGuestPhone(contact.phone);
}

export function PublicPlayEventJoinSection({
  orgSlug,
  eventSlug,
  event,
  participants: _participants,
  clubSlug,
  academyId,
  orgRegion,
  pathFor,
  onJoined,
}: PublicPlayEventJoinSectionProps) {
  const { user } = useAuthStore();
  const { profile: orgProfile } = useOrgPublicProfile();
  const orgRegister = useOrgRegisterDialogOptional();
  const returnPath = `${pathFor('/play')}?event=${encodeURIComponent(eventSlug)}`;
  const resolvedRegion =
    typeof orgRegion === 'string' ? (orgRegion as Region) : (orgRegion ?? orgProfile?.organization.region ?? undefined);
  const defaultPhoneCountry = useDefaultPhoneCountry({
    country: orgProfile?.organization.country ?? undefined,
    region: resolvedRegion ?? undefined,
  });

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [joinOptions, setJoinOptions] = useState<JoinModeOption[]>([]);
  const [selectedMode, setSelectedMode] = useState<JoinMode | null>(null);
  const [guestFirstName, setGuestFirstName] = useState(() => user?.firstName ?? '');
  const [guestLastName, setGuestLastName] = useState(() => user?.lastName ?? '');
  const [guestEmail, setGuestEmail] = useState(() => user?.email ?? '');
  const [guestPhone, setGuestPhone] = useState(() => user?.phone ?? '');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [gender, setGender] = useState<ParticipantGender | ''>('');
  const [joining, setJoining] = useState(false);
  const [settingUpStudent, setSettingUpStudent] = useState(false);
  const [needsGender, setNeedsGender] = useState(false);
  const [paymentStep, setPaymentStep] = useState<{
    clientSecret: string;
    stripeConnectAccountId: string;
    amountCents: number;
    currency: string;
  } | null>(null);
  const [myRegistration, setMyRegistration] = useState<MyPlayEventRegistration | null>(null);

  const currency = getCurrencyFromRegion(
    typeof orgRegion === 'string' ? (orgRegion as Region) : (orgRegion ?? undefined)
  ).toLowerCase();

  const hasAcademy = Boolean(academyId && orgProfile?.modules.hasAcademy);
  const registerMode: OrgRegisterMode = resolvePlayEventRegisterMode(event.joinPolicy, hasAcademy);
  const needsAcademyStudent = playEventNeedsAcademyStudent(event.joinPolicy);

  const loadMyRegistration = useCallback(async () => {
    if (!user) {
      setMyRegistration(null);
      return;
    }
    const res = await playEventService.getMyRegistrations(orgSlug);
    if (res.success && res.data) {
      setMyRegistration(res.data.find((row) => row.event.id === event.id) ?? null);
    }
  }, [user, orgSlug, event.id]);

  useEffect(() => {
    void loadMyRegistration();
  }, [loadMyRegistration]);

  const loadJoinOptions = useCallback(async () => {
    if (!user) return;
    setLoadingOptions(true);
    try {
      const options: JoinModeOption[] = [];
      let guestContact = resolveGuestContactFromUser(user);

      if (allowsMember(event.joinPolicy) && clubSlug) {
        const membershipRes = await publicClubMembershipService.getMyMembership(clubSlug);
        if (membershipRes.success && membershipRes.data) {
          const activeMembership = membershipRes.data.memberships.find(
            (membership) => membership.status === 'active'
          );
          const primarySeat =
            activeMembership?.seats.find((seat) => seat.isPrimary) ?? activeMembership?.seats[0];
          if (primarySeat) {
            options.push({
              mode: 'member',
              memberId: primarySeat.memberId,
              label: `Join as member (${primarySeat.firstName} ${primarySeat.lastName})`,
            });
            guestContact = {
              ...guestContact,
              firstName: primarySeat.firstName || guestContact.firstName,
              lastName: primarySeat.lastName || guestContact.lastName,
            };
          }
        }
      }

      if (allowsStudent(event.joinPolicy) && academyId) {
        const studentsRes = await studentService.getStudents(academyId);
        if (studentsRes.success && studentsRes.data) {
          const selfStudent = studentsRes.data.find((student) => student.isSelf);
          if (selfStudent) {
            options.push({
              mode: 'student',
              studentId: selfStudent.id,
              label: `Join as ${selfStudent.firstName} ${selfStudent.lastName}`,
            });
            guestContact = {
              ...guestContact,
              firstName: selfStudent.firstName || guestContact.firstName,
              lastName: selfStudent.lastName || guestContact.lastName,
              phone: selfStudent.invitePhone || guestContact.phone,
            };
          } else {
            for (const student of studentsRes.data.filter((row) => !row.isSelf)) {
              options.push({
                mode: 'student',
                studentId: student.id,
                label: `Join for ${student.firstName} ${student.lastName}`,
              });
            }
          }
        }
      }

      if (orgProfile?.academySlug) {
        const profilesRes = await academyProfileService.getMyProfiles();
        if (profilesRes.success && profilesRes.data) {
          const academyProfile = profilesRes.data.find(
            (profile) => profile.academy?.slug === orgProfile.academySlug
          );
          if (academyProfile?.phone) {
            guestContact = {
              ...guestContact,
              phone: academyProfile.phone,
            };
          }
        }
      }

      applyGuestContactDetails(guestContact, {
        setGuestFirstName,
        setGuestLastName,
        setGuestEmail,
        setGuestPhone,
      });

      if (allowsGuest(event.joinPolicy)) {
        options.push({ mode: 'guest', label: 'Join as guest' });
      }

      setJoinOptions(options);
      setSelectedMode((current) => {
        if (current && options.some((option) => option.mode === current)) {
          return current;
        }
        return options[0]?.mode ?? null;
      });
    } finally {
      setLoadingOptions(false);
    }
  }, [academyId, clubSlug, event.joinPolicy, orgProfile?.academySlug, user]);

  useEffect(() => {
    if (!user) {
      setJoinOptions([]);
      setSelectedMode(null);
      setGuestFirstName('');
      setGuestLastName('');
      setGuestEmail('');
      setGuestPhone('');
      return;
    }
    applyGuestContactDetails(resolveGuestContactFromUser(user), {
      setGuestFirstName,
      setGuestLastName,
      setGuestEmail,
      setGuestPhone,
    });
    void loadJoinOptions();
  }, [loadJoinOptions, user]);

  const selectedOption = joinOptions.find((option) => option.mode === selectedMode) ?? null;

  const buildPayload = (): PlayEventJoinPayload | null => {
    if (!selectedOption) return null;

    if (selectedOption.mode === 'member') {
      if (!selectedOption.memberId) return null;
      return {
        participantType: 'member',
        memberId: selectedOption.memberId,
        ...(gender ? { gender } : {}),
      };
    }

    if (selectedOption.mode === 'student') {
      if (!selectedOption.studentId) return null;
      return {
        participantType: 'student',
        studentId: selectedOption.studentId,
        ...(gender ? { gender } : {}),
      };
    }

    if (!guestFirstName.trim() || !guestLastName.trim()) {
      toast.error('Please enter your name');
      return null;
    }
    if (!guestEmail.trim() && !guestPhone.trim()) {
      toast.error('Please enter an email or phone number');
      return null;
    }
    if (!gender) {
      toast.error('Please select your gender');
      return null;
    }

    let contactPhone: string | undefined;
    if (guestPhone.trim()) {
      const guestPhoneE164 = normalizePhoneToE164(guestPhone, defaultPhoneCountry);
      if (!guestPhoneE164 || !isValidPhone(guestPhone, defaultPhoneCountry)) {
        setPhoneError('Enter a valid phone number');
        toast.error('Enter a valid phone number');
        return null;
      }
      contactPhone = guestPhoneE164;
    }

    return {
      participantType: 'guest',
      guestFirstName: guestFirstName.trim(),
      guestLastName: guestLastName.trim(),
      ...(guestEmail.trim() ? { contactEmail: guestEmail.trim() } : {}),
      ...(contactPhone ? { contactPhone } : {}),
      gender,
    };
  };

  const handleJoinResponse = (data: PlayEventJoinResponse) => {
    const amountCents = data.finalPriceCents ?? data.totalCents ?? 0;
    if (data.clientSecret && data.stripeConnectAccountId && amountCents > 0) {
      setPaymentStep({
        clientSecret: data.clientSecret,
        stripeConnectAccountId: data.stripeConnectAccountId,
        amountCents,
        currency: data.currency ?? currency,
      });
      return;
    }
    toast.success('You are registered for this event');
    void loadMyRegistration();
    onJoined();
  };

  const handleSetupAcademyStudent = async () => {
    const academySlug = orgProfile?.academySlug;
    if (!academySlug || !user) return;

    setSettingUpStudent(true);
    try {
      const res = await academyProfileService.createProfile({
        academySlug,
        firstName: user.firstName?.trim() || 'Student',
        lastName: user.lastName?.trim() || 'User',
        email: user.email,
        ensureSelfStudentRosterEntry: true,
      });
      if (res.success) {
        await loadJoinOptions();
        toast.success('You can now join as an academy student.');
        return;
      }
      toast.error(res.error ?? 'Could not complete academy student registration');
    } catch {
      toast.error('Could not complete academy student registration');
    } finally {
      setSettingUpStudent(false);
    }
  };

  const handleJoin = async () => {
    const payload = buildPayload();
    if (!payload) return;

    setJoining(true);
    try {
      const res = await playEventService.joinPublicEvent(orgSlug, eventSlug, payload);
      if (res.success && res.data) {
        setNeedsGender(false);
        handleJoinResponse(res.data);
      } else if (res.error && /gender is required/i.test(res.error)) {
        setNeedsGender(true);
        toast.error('Please select your gender to continue');
      } else {
        toast.error(res.error ?? 'Could not join event');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Could not join event';
      if (/gender is required/i.test(message)) {
        setNeedsGender(true);
        toast.error('Please select your gender to continue');
      } else {
        toast.error(message);
      }
    } finally {
      setJoining(false);
    }
  };

  if (!user) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Sign in to register. For <strong className="font-medium text-foreground">open</strong>{' '}
          events you can join as a guest after signing in — no club membership required.
        </p>
        <p className="text-sm text-muted-foreground">
          Use the same email on your account as in the registration form so your matches appear
          under <strong className="font-medium text-foreground">My matches</strong> after the draw
          is published.
        </p>
        <OrgSignInLink
          returnUrl={returnPath}
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Sign in to join
        </OrgSignInLink>
        {orgRegister ? (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => orgRegister.openOrgRegister(returnPath, { mode: registerMode })}
          >
            {registerMode === 'academy' ? 'Create student account' : 'Create account'}
          </Button>
        ) : (
          <Button asChild variant="outline" className="w-full">
            <Link to={pathFor(`/register?returnUrl=${encodeURIComponent(returnPath)}`)}>
              {registerMode === 'academy' ? 'Create student account' : 'Create account'}
            </Link>
          </Button>
        )}
      </div>
    );
  }

  if (myRegistration) {
    return (
      <Alert>
        <AlertDescription>
          You are registered for this event
          {unpaidRegistrationMessage(event, myRegistration) ?? '.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (paymentStep) {
    return (
      <PublicStripePaymentConfirm
        clientSecret={paymentStep.clientSecret}
        stripeConnectAccountId={paymentStep.stripeConnectAccountId}
        amountCents={paymentStep.amountCents}
        currency={paymentStep.currency}
        onSuccess={() => {
          toast.success('Payment complete — you are registered for this event');
          setPaymentStep(null);
          void loadMyRegistration();
          onJoined();
        }}
        onCancel={() => setPaymentStep(null)}
      />
    );
  }

  if (loadingOptions) {
    return (
      <div className="flex min-h-[88px] items-center justify-center" aria-busy="true">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (joinOptions.length === 0) {
    let message = 'You are not eligible to join this event with your current account.';
    if (event.joinPolicy === 'members_only') {
      message = 'This event is for active club members.';
    } else if (event.joinPolicy === 'students_only') {
      message = 'This event is for academy students.';
    } else if (event.joinPolicy === 'members_and_students') {
      message = 'This event is for club members and academy students.';
    }

    const signedInNeedsStudentSetup = Boolean(user && needsAcademyStudent && orgProfile?.academySlug);

    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {signedInNeedsStudentSetup
            ? 'Your account is signed in, but you are not registered as an academy student at this organization yet.'
            : message}
        </p>
        {event.joinPolicy !== 'students_only' && clubSlug ? (
          <Button asChild variant="outline" className="w-full">
            <Link to={pathFor(`/join`)}>View membership options</Link>
          </Button>
        ) : null}
        {event.joinPolicy !== 'members_only' && academyId ? (
          signedInNeedsStudentSetup ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => void handleSetupAcademyStudent()}
              isLoading={settingUpStudent}
            >
              Register as academy student
            </Button>
          ) : orgRegister ? (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => orgRegister.openOrgRegister(returnPath, { mode: 'academy' })}
            >
              Create student account
            </Button>
          ) : (
            <Button asChild variant="outline" className="w-full">
              <Link to={pathFor(`/register?returnUrl=${encodeURIComponent(returnPath)}`)}>
                Create student account
              </Link>
            </Button>
          )
        ) : null}
      </div>
    );
  }

  const showGuestForm = selectedOption?.mode === 'guest';
  const showModePicker = joinOptions.length > 1;

  return (
    <div className="space-y-4">
      {showModePicker ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Register as</p>
          <div className="flex flex-col gap-2">
            {joinOptions.map((option) => (
              <button
                key={`${option.mode}-${option.memberId ?? option.studentId ?? 'guest'}`}
                type="button"
                onClick={() => setSelectedMode(option.mode)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  selectedMode === option.mode
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {showGuestForm ? (
        <div className="space-y-3">
          {needsGender ? (
            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
              <AlertDescription>
                Please select your gender to register for this event. It is used for match pairings and fairness.
              </AlertDescription>
            </Alert>
          ) : null}
          <p className="text-sm text-muted-foreground">
            Joining as a guest. Use the same email as your signed-in account so your matches appear
            under My matches.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="public-play-guest-first">First name</FieldLabel>
              <Input
                id="public-play-guest-first"
                value={guestFirstName}
                onChange={(e) => setGuestFirstName(e.target.value)}
                autoComplete="given-name"
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="public-play-guest-last">Last name</FieldLabel>
              <Input
                id="public-play-guest-last"
                value={guestLastName}
                onChange={(e) => setGuestLastName(e.target.value)}
                autoComplete="family-name"
                required
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="public-play-guest-email">Email</FieldLabel>
            <Input
              id="public-play-guest-email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              autoComplete="email"
            />
            <FieldDescription>Required if you do not provide a phone number.</FieldDescription>
          </Field>
          <Field>
            <PhoneNumberLabel htmlFor="public-play-guest-phone" purpose="general">
              Phone
            </PhoneNumberLabel>
            <PhoneInput
              id="public-play-guest-phone"
              value={guestPhone}
              onChange={(value) => {
                setGuestPhone(value);
                if (phoneError) setPhoneError(null);
              }}
              defaultCountry={defaultPhoneCountry}
              defaultCountrySource={{
                country: orgProfile?.organization.country ?? undefined,
                region: resolvedRegion ?? undefined,
              }}
              error={!!phoneError}
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'public-play-guest-phone-error' : undefined}
            />
            {phoneError ? (
              <p id="public-play-guest-phone-error" className="text-sm text-destructive" role="alert">
                {phoneError}
              </p>
            ) : null}
          </Field>
          <GenderField
            id="public-play-guest-gender"
            value={gender}
            onChange={setGender}
            required
          />
        </div>
      ) : needsGender ? (
        <GenderField
          id="public-play-member-gender"
          value={gender}
          onChange={setGender}
          required
          description="Gender is required for match pairing."
        />
      ) : null}

      <Button className="w-full" onClick={() => void handleJoin()} isLoading={joining}>
        {event.paymentMode === 'online_on_join' ? 'Continue to registration' : 'Join event'}
      </Button>
    </div>
  );
}

function GenderField({
  id,
  value,
  onChange,
  required,
  description,
}: {
  id: string;
  value: ParticipantGender | '';
  onChange: (value: ParticipantGender) => void;
  required?: boolean;
  description?: string;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>Gender{required ? ' *' : ''}</FieldLabel>
      <Select value={value || undefined} onValueChange={(next) => onChange(next as ParticipantGender)}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Select gender" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="male">Male</SelectItem>
          <SelectItem value="female">Female</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </Field>
  );
}
